import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import moment from 'moment-timezone';
import strings from 'strings';
import { getTimeInputValidity } from 'UI/utils';

import { getQuotaRestriction } from '../../BulkEmailDrawer/utils';
import { BulkDrawerFormFieldsMap, scheduleKeys } from '../../BulkEmailForm/fields';

const {
  schedule: { errors: errorsCopies },
  dialogs: { scheduleForm: scheduleFormCopies }
} = strings.bulkEmails;

/**
 * @param {Object} props
 * @param {boolean} props.isBulkInPreviewBeingEdited
 * @param {Function} props.onSubmitBulkEmail
 * @param {Function} props.notifyBulkQuotaRestriction
 * @param {string} props.sendDate
 * @param {boolean} props.shouldUnregisterFieldsOnClose
 * @param {number} props.remainingQuota
 * @param {number} props.searchProjectSize
 */
export const useScheduleBulkDialog = ({
  isBulkInPreviewBeingEdited,
  sendDate,
  shouldUnregisterFieldsOnClose,
  remainingQuota,
  searchProjectSize,
  onSubmitBulkEmail,
  notifyBulkQuotaRestriction
}) => {
  const {
    clearError,
    errors,
    getValues,
    handleSubmit,
    register,
    setError,
    setValue,
    triggerValidation,
    unregister,
    watch
  } = useFormContext();
  const formValues = watch(scheduleKeys);

  const bulkQuotaRestriction = getQuotaRestriction({ remainingQuota, searchProjectSize });
  const isCurrentDay = formValues[BulkDrawerFormFieldsMap.ScheduleDate.key]?.isSame(
    moment(),
    'day'
  );

  useEffect(() => {
    register(
      { name: BulkDrawerFormFieldsMap.ScheduleDate.key },
      {
        required: strings.formatString(scheduleFormCopies.validations.required, {
          moment: scheduleFormCopies.validations.refs.date
        })
      }
    );
    register(
      { name: BulkDrawerFormFieldsMap.ScheduleTime.key },
      {
        required: strings.formatString(scheduleFormCopies.validations.required, {
          moment: scheduleFormCopies.validations.refs.time
        })
      }
    );
  }, [register, unregister]);

  useEffect(() => {
    return () => {
      if (shouldUnregisterFieldsOnClose) {
        unregister(scheduleKeys);
      }
    };
  }, [unregister, shouldUnregisterFieldsOnClose]);

  useEffect(() => {
    if (!isBulkInPreviewBeingEdited || !sendDate) return;
    const currentSendDate = moment(sendDate);
    scheduleKeys.forEach(key => {
      setValue(key, currentSendDate);
    });
  }, [isBulkInPreviewBeingEdited, sendDate, setValue]);

  const setTimeInputErrors = isValid => {
    if (isValid)
      return (
        errors[BulkDrawerFormFieldsMap.ScheduleTime.key] &&
        clearError(BulkDrawerFormFieldsMap.ScheduleTime.key)
      );

    setValue(BulkDrawerFormFieldsMap.ScheduleTime.key, null);
    return setError(
      BulkDrawerFormFieldsMap.ScheduleTime.key,
      'isBeforeActualDateTime',
      errorsCopies.beforeActualDateTime
    );
  };

  const handleDateChange = (name, date) => {
    const isTimeInputValid = getTimeInputValidity(
      date,
      getValues(BulkDrawerFormFieldsMap.ScheduleTime.key)
    );
    setTimeInputErrors(isTimeInputValid);
    setValue(name, date, true);
  };

  const handleTimeChange = (name, time) => {
    const isTimeInputValid = getTimeInputValidity(
      getValues(BulkDrawerFormFieldsMap.ScheduleDate.key),
      time
    );
    setTimeInputErrors(isTimeInputValid);
    isTimeInputValid && setValue(name, time, true);
  };

  const submitHandler = async ok => {
    await triggerValidation(scheduleKeys);
    const errorsIsEmpty = Object.keys(errors).length === 0;
    if (!errorsIsEmpty) return;

    if (remainingQuota === 0 && isCurrentDay) {
      notifyBulkQuotaRestriction && notifyBulkQuotaRestriction();
      return;
    }

    ok &&
      handleSubmit(formData =>
        onSubmitBulkEmail(formData, {
          saveAsSchedule: true
        })
      )();
  };

  return {
    formValues,
    errors,
    bulkQuotaRestriction,
    handleDateChange,
    handleTimeChange,
    submitHandler
  };
};
